/**
 * Story filter comparators per value type, as pure data: the backend's
 * comparator × value_type table for `POST /filters` (and `PATCH`), in operator
 * menu order. No API lists these, so this copy is the frontend's single source
 * for the operator menu, the value editor's shape and the mock's validation. A
 * change to the backend table is an edit to this file only.
 *
 * `relative_date` is in no list: the date presets send it (a `between` whose
 * dates are a preset's range today saves as that preset's rolling period, see
 * `storyFiltersMappers`), and a saved one reopens as `between`. `contains` is
 * for text fields only.
 */

import type { FilterComparator, FilterValueType } from "../../services/storyCanvas/storyFilters.types";

/** What the value editor asks for: one value, a From/To pair, a list (1 to `storyFilterListLimit` values), or nothing. */
export type FilterValueShape = "one" | "pair" | "list" | "none";

/** The most values the backend takes in one `in` / `not_in` list. */
export const storyFilterListLimit = 200;

export interface StoryFilterComparatorConfig {
  comparator: FilterComparator;
  /** Operator menu copy. */
  label: string;
  valueShape: FilterValueShape;
  /**
   * The backend takes it, but the menu leaves it out: a saved filter with it
   * opens as this comparator instead (a one-value `is` already sends `in`).
   */
  opensAs?: FilterComparator;
  /** Not on a measure: the backend's number `in` / `not_in` are for numeric dimension attributes only. */
  dimensionOnly?: boolean;
}

export const storyFilterComparators: Record<FilterValueType, readonly StoryFilterComparatorConfig[]> = {
  categorical: [
    { comparator: "in", label: "is", valueShape: "list" },
    { comparator: "not_in", label: "is not", valueShape: "list" },
    { comparator: "contains", label: "contains", valueShape: "one" },
    { comparator: "is_null", label: "is empty", valueShape: "none" },
    { comparator: "eq", label: "is", valueShape: "one", opensAs: "in" },
    { comparator: "neq", label: "is not", valueShape: "one", opensAs: "not_in" },
  ],
  temporal: [
    { comparator: "eq", label: "on", valueShape: "one" },
    { comparator: "neq", label: "not on", valueShape: "one" },
    { comparator: "lt", label: "before", valueShape: "one" },
    { comparator: "lte", label: "on or before", valueShape: "one" },
    { comparator: "gt", label: "after", valueShape: "one" },
    { comparator: "gte", label: "on or after", valueShape: "one" },
    { comparator: "between", label: "between", valueShape: "pair" },
    { comparator: "in", label: "is any of", valueShape: "list" },
    { comparator: "not_in", label: "is none of", valueShape: "list" },
    { comparator: "is_null", label: "is empty", valueShape: "none" },
  ],
  continuous: [
    { comparator: "eq", label: "=", valueShape: "one" },
    { comparator: "neq", label: "≠", valueShape: "one" },
    { comparator: "lt", label: "<", valueShape: "one" },
    { comparator: "lte", label: "≤", valueShape: "one" },
    { comparator: "gt", label: ">", valueShape: "one" },
    { comparator: "gte", label: "≥", valueShape: "one" },
    { comparator: "between", label: "between", valueShape: "pair" },
    { comparator: "in", label: "is any of", valueShape: "list", dimensionOnly: true },
    { comparator: "not_in", label: "is none of", valueShape: "list", dimensionOnly: true },
    { comparator: "is_null", label: "is empty", valueShape: "none" },
  ],
};
